import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CompanyProvider } from './context/CompanyContext';
import { CompanyUsersProvider } from './context/CompanyUsersContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { NavigationProvider } from './context/NavigationContext';
import { AutomationProvider } from './context/AutomationContext';
import ProtectedRoute, { LoadingScreen, useScrollLock } from './components/ProtectedRoute';
import { useAuthDestination, DESTINATION, DESTINATION_PATH } from './hooks/useAuthDestination';
import { hasSessionHint } from './utils/sessionHint';

// ── Route-level code splitting ───────────────────────────────────────────────
// Every page is its own chunk. Before this, main.js was a single 2.18MB raw /
// 553kB gzipped bundle, so a visitor to the marketing page downloaded
// Dashboard, Settings, Clients, Cruncher and jsPDF before seeing a word of it
// (BONEFLOW_VISION, Landing Page Mobile Pass, item 1).
//
// ProtectedRoute stays a STATIC import on purpose. It is ~13kB of source whose
// only dependencies are two contexts already in the main chunk, and it is the
// parent route element for every authenticated page: lazy there would put a
// second, SERIAL chunk fetch in front of the Dashboard's own, because React
// can't request the child's chunk until the parent has rendered its Outlet.
// That trades a couple of kB for a whole round trip on the app's main entry.
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const GuidedOnboarding = lazy(() => import('./components/GuidedOnboarding'));
const ActionPage = lazy(() => import('./pages/ActionPage'));
// "/" is the umbrella page (UmbrellaPage) since 2026-10-17; the window-and-door
// landing page it replaced is LandingPage, at /windows-doors.
const UmbrellaPage = lazy(() => import('./pages/UmbrellaPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const AccountReviewPage = lazy(() => import('./pages/AccountReviewPage'));

// Backdrop for the chunk splash. Each public page owns a FIXED palette that
// ignores the saved theme — the two landing pages are white paper, signup is
// dark by design, /login and /action are the light field-notes look — so the
// splash has to match the page it is standing in for or the handoff flashes.
// Everything else is the app, which follows the same localStorage key
// index.html's boot script and LoadingScreen already read.
// The app's own paper, from the same localStorage key index.html's boot script
// and LoadingScreen already read.
function appBackground() {
  try {
    return localStorage.getItem('boneflow_theme') === 'night' ? '#0e0d0b' : '#f4f1eb';
  } catch {
    return '#f4f1eb';
  }
}

function splashBackground(pathname) {
  // Both landing pages are white paper (the Drafting Table). "/" splashed on
  // the OLD dark hero's #0d0d0d long after the page went light — a black frame
  // between the boot script's off-white and the page's white — until the route
  // split (2026-10-17) gave it a sibling and both took the page's own ground.
  if (pathname === '/' || pathname.startsWith('/windows-doors')) return '#ffffff';
  // The account-gate screens wear signup's dark page — they are its next two
  // steps, and a light frame between them would flash.
  if (pathname.startsWith('/signup')
      || pathname.startsWith('/verify-email') || pathname.startsWith('/account-review')) return '#0d0d0d';
  if (pathname.startsWith('/login') || pathname.startsWith('/action')) return '#f4f1eb';
  return appBackground();
}

// Suspense fallback for a route chunk in flight. Deliberately NOT LoadingScreen:
// that one means "auth/company state is resolving" and is part of the app, while
// this one means "the code is still on the wire" and is the only thing on screen
// before the very first page paints — so it is the smallest possible thing.
// `background` overrides the per-path default. RootRoute needs it: by pathname
// it is "/" (the white umbrella page), but when it shows this splash it is on
// its way to the DASHBOARD, so it has to hand off to the app's paper instead —
// otherwise a night-mode user gets a white frame before a near-black one.
function RouteSplash({ background }) {
  const { pathname } = useLocation();
  // Same standing-still rule as LoadingScreen — see the note there. This one
  // is on screen for public routes too, where there is equally nothing to
  // scroll.
  useScrollLock();
  // The spinner's leading arc is BRAND CHROME, so it follows the resolved
  // per-user accent (personal pick → company accent → platform gold), the same
  // value pipeline stage indicators use — NOT theme.accent, which is the
  // mode-based ink/paper swap. On the public routes nobody is signed in, so it
  // resolves to the platform default and looks exactly as it always has.
  const { accentColor } = useTheme();
  return (
    <div className="bf-viewport-fill" style={{
      backgroundColor: background || splashBackground(pathname),
      overflow: 'hidden',
      overscrollBehavior: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Keyframes co-located and prefixed, matching every other animation in
          the app (bfpPulse, bfsiPulse, bfLpMenuIn) — a bare `spin` in
          index.css would be one name away from a collision. */}
      <style>{
        '@keyframes bfRouteSpin{to{transform:rotate(360deg)}}'
        + '@media (prefers-reduced-motion:reduce){.bf-route-spin{animation:none}}'
      }</style>
      <div
        className="bf-route-spin"
        style={{
          width: 32,
          height: 32,
          border: '2px solid #333333',
          borderTopColor: accentColor,
          borderRadius: '50%',
          animation: 'bfRouteSpin 0.8s linear infinite',
        }}
      />
    </div>
  );
}

// "/" — the public umbrella page (UmbrellaPage: the industry chooser, since
// 2026-10-17 — the window-and-door page that used to be here is at
// /windows-doors), and the one route that renders BEFORE Firebase Auth has
// answered. A signed-out visitor is the only person this page is for and has
// no use for that round trip, so gating the first paint on it put a 300–800ms
// LoadingScreen in front of the one page whose entire job is to load fast.
//
// The redirect for authenticated users happens in an effect once the session
// SETTLES, rather than blocking the render. Same optimistic render +
// effect-redirect shape SignupPage already uses.
//
// It now asks useAuthDestination, like every other gate. This route used to
// decide on companyId alone and hand anyone who had one to /dashboard —
// correct destination for most, but a company still mid-setup was then bounced
// straight back to /onboarding by ProtectedRoute. One redirect, one answer.
//
// THE OLD TRADEOFF, NOW MOSTLY BOUGHT BACK. Optimistic rendering meant a
// signed-in user who lands on "/" saw the marketing page for ~300ms before
// being bounced — accepted because nothing in the app links to "/" and a
// first-time visitor is the common case. It is still jarring every time you
// open the app during development.
//
// The flash was unavoidable only because the sole answer to "is this person
// signed in" was ASYNCHRONOUS. utils/sessionHint.js adds a synchronous, purely
// local, non-authoritative hint, which lets this route decide what to paint on
// the FIRST tick:
//   hint present → a spinner on the app's paper, never the marketing page, so
//     the handoff to /dashboard is clean. If auth then answers "nobody", the
//     hint was stale and we fall through to the landing page.
//   hint absent  → UmbrellaPage immediately, exactly as before. A genuine
//     marketing visitor still never waits on an auth round trip, which was the
//     entire point of the original design.
function RootRoute() {
  const destination = useAuthDestination();
  const navigate = useNavigate();

  // Read ONCE, synchronously, at first render — before any auth work starts.
  // In a useState initializer rather than inline so a mid-flight write (the
  // AuthContext sync, or a logout in another tab) can't change this component's
  // mind about what it is rendering half way through resolving.
  const [sessionHint] = useState(hasSessionHint);

  useEffect(() => {
    // PENDING navigates nowhere — that is the whole point of it being an
    // answer. LOGIN falls through to the landing page below.
    if (destination !== DESTINATION.PENDING && destination !== DESTINATION.LOGIN) {
      navigate(DESTINATION_PATH[destination], { replace: true });
    }
  }, [destination, navigate]);

  // Auth has answered, and the answer is "nobody" — the hint was stale (session
  // expired, revoked, or ended in another tab). Nothing more to wait for, so
  // fall through to the landing page. AuthContext has already cleared the hint,
  // so the next visit skips the spinner entirely.
  const hintWasStale = destination === DESTINATION.LOGIN;

  // The ONLY case that withholds the landing page. A visitor with no hint —
  // every genuine first-time arrival at boneflow.co, the case the whole
  // optimistic-render design exists for — never reaches this branch and never
  // waits on an auth round trip. Someone signed in but WITHOUT a hint (a
  // session predating this hint, on its one migration load) also falls through
  // to exactly the old behavior: a brief landing flash, then the redirect, and
  // the hint written for next time.
  if (sessionHint && !hintWasStale) return <RouteSplash background={appBackground()} />;

  return <UmbrellaPage />;
}

// /login — already-authenticated users are bounced to where they belong, and
// THIS IS ALSO WHERE A FRESH SIGN-IN LANDS.
//
// Login.js used to navigate('/dashboard') the instant
// signInWithEmailAndPassword() resolved. That is a promise resolving, not the
// session settling: the profile and company reads had not happened yet, so the
// app was already at /dashboard while the very state that decides where the
// user belongs was still in flight — and it threw away the deep link that sent
// them here. The form now only signs in; the redirect happens HERE, when the
// destination is actually known.
//
// `state.from` is set by ProtectedRoute when it turns a signed-out visitor away
// from a real page — an emailed link to a job or a task, almost always. Falls
// back to /dashboard for anyone who came to /login directly.
function LoginRoute() {
  const destination = useAuthDestination();
  const location = useLocation();
  const from = location.state?.from;
  const target = from ? `${from.pathname}${from.search || ''}` : '/dashboard';

  if (destination === DESTINATION.PENDING) return <LoadingScreen />;
  if (destination === DESTINATION.APP) return <Navigate to={target} replace />;
  if (destination !== DESTINATION.LOGIN) return <Navigate to={DESTINATION_PATH[destination]} replace />;
  return <Login />;
}

// /onboarding — covers both onboarding states: company creation (no
// companyId on the profile yet) and the guided pipeline setup wizard
// (company exists but onboardingComplete === false). Once both are done,
// replace-redirect to the dashboard so back never returns here.
function OnboardingRoute() {
  const destination = useAuthDestination();
  const { userProfile } = useAuth();

  if (destination === DESTINATION.PENDING) return <LoadingScreen />;
  if (destination !== DESTINATION.ONBOARDING) return <Navigate to={DESTINATION_PATH[destination]} replace />;
  // ONBOARDING — which of the two steps is decided by the same field the
  // resolver used to get here: no company yet is step one, an unfinished
  // company is step two.
  return userProfile?.companyId ? <GuidedOnboarding /> : <Onboarding />;
}

// /verify-email and /account-review — the account gate's two screens. Each
// renders only while the resolver says this is where the session belongs, and
// otherwise forwards to wherever that is. That forward IS how both screens
// "move on by themselves": the email is confirmed, or the company is approved,
// the resolver's answer changes, and this element navigates.
function GateRoute({ destination: own, children }) {
  const destination = useAuthDestination();
  if (destination === DESTINATION.PENDING) return <LoadingScreen />;
  if (destination !== own) return <Navigate to={DESTINATION_PATH[destination]} replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
          {/* One company-users read, shared by every page that used to run its
              own (see CompanyUsersContext). Inside CompanyProvider only for
              ordering readability — it keys off userProfile.companyId from
              AuthContext, not off the loaded company doc. */}
          <CompanyUsersProvider>
          <ThemeProvider>
            <NavigationProvider>
              <AutomationProvider>
                {/* One boundary around the whole table: every route element
                    below is lazy, and the routes that aren't (LoginRoute,
                    OnboardingRoute) still render lazy pages underneath. */}
                <Suspense fallback={<RouteSplash />}>
                  <Routes>
                    {/* Public, no-login magic-link confirmation page (5.20). Must
                        stay outside ProtectedRoute — email recipients may not be
                        authenticated. */}
                    <Route path="/action" element={<ActionPage />} />
                    {/* Public umbrella page — signed-out visitors see the
                        marketing page immediately; authenticated users are
                        redirected once auth resolves (see RootRoute). */}
                    <Route path="/" element={<RootRoute />} />
                    {/* The window-and-door landing page, moved here intact
                        from "/" when "/" became the umbrella page
                        (2026-10-17). PUBLIC TO EVERYONE, with no redirect:
                        "/" bounces a signed-in user because it is the app's
                        front door (bookmarks, the home-screen icon), while
                        this page is only ever reached by a deliberate link,
                        so it shows what was asked for. */}
                    <Route path="/windows-doors" element={<LandingPage />} />
                    <Route path="/login" element={<LoginRoute />} />
                    {/* Public self-serve signup (5.41). No wrapper route: the
                        page redirects already-authenticated visitors itself,
                        because mid-signup the flow SIGNS THE USER IN (auth user
                        created at Step 2 submit) and must not be yanked to
                        /dashboard before the company + profile records exist. */}
                    <Route path="/signup" element={<SignupPage />} />
                    {/* The account gate (2026-10-17): "check your email",
                        then "we're reviewing your account". Outside
                        ProtectedRoute for the same reason /onboarding is —
                        the app shell must not mount for either. */}
                    <Route path="/verify-email" element={<GateRoute destination={DESTINATION.VERIFY_EMAIL}><VerifyEmailPage /></GateRoute>} />
                    <Route path="/account-review" element={<GateRoute destination={DESTINATION.ACCOUNT_REVIEW}><AccountReviewPage /></GateRoute>} />
                    <Route path="/onboarding" element={<OnboardingRoute />} />
                    <Route element={<ProtectedRoute />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/workflows" element={<Dashboard />} />
                      <Route path="/workflows/:jobId" element={<Dashboard />} />
                      <Route path="/clients" element={<Dashboard />} />
                      <Route path="/clients/:clientId" element={<Dashboard />} />
                      <Route path="/clients/:clientId/jobs/:jobId" element={<Dashboard />} />
                      <Route path="/clients/:clientId/jobs/:jobId/:tab" element={<Dashboard />} />
                      <Route path="/clients/:clientId/tickets/:ticketId" element={<Dashboard />} />
                      <Route path="/tasks" element={<Dashboard />} />
                      <Route path="/analytics" element={<Dashboard />} />
                      <Route path="/financials" element={<Dashboard />} />
                      <Route path="/cruncher" element={<Dashboard />} />
                      <Route path="/cruncher/:outputId" element={<Dashboard />} />
                      <Route path="/settings" element={<Dashboard />} />
                      <Route path="/settings/job-types/:jobTypeId" element={<Dashboard />} />
                      <Route path="/settings/pricing/:presetId" element={<Dashboard />} />
                      <Route path="/settings/price-book/:manufacturerId" element={<Dashboard />} />
                      <Route path="/settings/import" element={<Dashboard />} />
                      <Route path="/accounts" element={<Dashboard />} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                  </Routes>
                </Suspense>
              </AutomationProvider>
            </NavigationProvider>
          </ThemeProvider>
          </CompanyUsersProvider>
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
